import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import Icon from './Icon';
import InfoTip from './InfoTip';
import Expander from './Expander';
import { AgingHeroBody } from './InsightPanels';
import { cycleTime, inFlight, keepingUp, leadTime, throughput } from '../insights';
import { weeksFor } from '../insights-scope';
import { stillBlocked } from '../blocked';
import { unblockActions } from '../unblock';
import { livePhases } from '../phases';
import { isProjectMode, boardLifecycleTier } from '../lifecycle';
import { isInService } from '../handoff';
import { boardInvestmentMix, investmentLifecycleDefault } from '../investment';

/**
 * THE BOARD-METRICS STRIP — progress read at the top of the board (mock B).
 *
 * A board run without a project — a refactor, an incident, a support queue — has
 * no charter, no phases, no budget, so the Plan and the Portfolio say nothing
 * about it. This strip is that board's answer: every figure DERIVED from card
 * status and timestamps, no typed "% done". It is the compact sibling of the
 * Insights screen — the same aging-WIP hero component, the same throughput and
 * cycle-time maths — and its "Open full metrics" opens Insights scoped to this
 * board for the full set.
 *
 * WHO IT IS FOR, AND WHEN IT SHOWS. A board-run board (no project signals) gets
 * it OPEN; a project-run board gets it FOLDED, since that team lives in the Plan.
 * The mode is inferred from the same signals the Portfolio's `tracked` uses, and
 * the "Measure as a board / Measure as a project" control lets a team override it. Both
 * the mode and the fold are remembered in this browser, per board and per person
 * — one team's choice is not imposed on another, exactly like the board's own
 * group-by and sort.
 */

const DAY = 86_400_000;
/* The strip is a fixed rolling-28-day read; the adjustable window lives on the
   full Insights screen behind "Open full metrics". `weeksFor(28)` is 8 weeks —
   enough of a throughput trend to say "steady" against. */
const WINDOW_DAYS = 28;

type Mode = 'board' | 'project';
const modeKey = (id: string) => `fd.bmstrip.mode.${id}`;
const foldKey = (id: string) => `fd.bmstrip.fold.${id}`;
const read = (k: string): string | null => {
  try { return localStorage.getItem(k); } catch { return null; }
};
const write = (k: string, v: string) => {
  try { localStorage.setItem(k, v); } catch { /* private browsing — forgotten next load, no more */ }
};

export default function BoardMetricsStrip() {
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const project = projects.find((p) => p.id === projectId);
  const issues = useApp((s) => s.issues);
  const issueTypes = useApp((s) => s.issueTypes);
  const phases = useApp((s) => s.phases);
  const builds = useApp((s) => s.builds);
  const profiles = useApp((s) => s.profiles);
  const openIssue = useApp((s) => s.openIssue);
  const showInBacklog = useApp((s) => s.showInBacklog);
  const setView = useApp((s) => s.setView);
  const drillTo = useApp((s) => s.drillTo);
  const setInsightsScope = useApp((s) => s.setInsightsScope);

  /*
   * BOARD OR PROJECT — inferred from the same signals the Portfolio's `tracked`
   * flag uses: a held project, a go-live, any phase, or a budget baseline. A
   * board with none of these is being run as a plain board, and gets the strip
   * open. The per-person override below switches how the strip is MEASURED
   * without changing the board.
   */
  const inferred: Mode =
    project?.heldAt || project?.goLive != null || livePhases(phases).length > 0 || project?.budget != null
      ? 'project'
      : 'board';

  const [modeOverride, setModeOverride] = useState<Mode | null>(null);
  const [foldPref, setFoldPref] = useState<'open' | 'folded' | null>(null);
  /* Re-read both when the open board changes — the strip is remembered per board,
     and switching boards must not carry the last one's choice across. */
  useEffect(() => {
    const m = read(modeKey(projectId));
    setModeOverride(m === 'board' || m === 'project' ? m : null);
    const f = read(foldKey(projectId));
    setFoldPref(f === 'open' || f === 'folded' ? f : null);
  }, [projectId]);

  /* A board RUN as a continuous service has no plan, phases or budget to measure
     a project against, so it can only be measured as a board — the per-person
     "project" override and the "See Plan" link are clamped off, or they offer a
     measurement and a destination that do not exist on this board (WF-F2). */
  const isProject = useApp((s) => isProjectMode(s.meta));
  // Once handed over, the strip reads the service, not a board — same metrics, a
  // name that matches the lifecycle (#435 surface 2).
  const inService = useApp((s) => isInService(s.meta));
  const mode: Mode = isProject ? (modeOverride ?? inferred) : 'board';
  /* A project-run board folds by default (that team works in the Plan); a
     board-run board opens. A viewer's own Hide / Show choice wins over that. */
  const folded = foldPref ? foldPref === 'folded' : mode === 'project';

  /* Switching how the board is RUN re-applies that mode's default presentation:
     run-as-project folds it (that team works in the Plan), run-as-board opens it.
     Without this, any earlier Hide/Show wrote a fold preference that permanently
     won over the mode default, so pressing the pill changed only its own label and
     nothing else — the workflow-witness caught that "does nothing" case (24 Sep). */
  const setMode = (m: Mode) => {
    write(modeKey(projectId), m);
    setModeOverride(m);
    try { localStorage.removeItem(foldKey(projectId)); } catch { /* nothing to clear it with */ }
    setFoldPref(null);
  };
  const setFold = (f: 'open' | 'folded') => { write(foldKey(projectId), f); setFoldPref(f); };

  /* The pill menu — Measure as a board / Measure as a project. Closes on a click away and
     on Escape, the app standard (see BacklogFilters, InsightsStrip). */
  const [menuOpen, setMenuOpen] = useState(false);
  const pill = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const away = (e: MouseEvent) => { if (!pill.current?.contains(e.target as Node)) setMenuOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [menuOpen]);

  const now = Date.now();
  const flight = useMemo(() => inFlight(issues, now, profiles), [issues, now, profiles]);
  const tp = useMemo(() => throughput(issues, now, weeksFor(WINDOW_DAYS)), [issues, now]);
  const cyc = useMemo(() => cycleTime(issues, now, WINDOW_DAYS), [issues, now]);
  const lead = useMemo(() => leadTime(issues, now, WINDOW_DAYS), [issues, now]);
  const keep = useMemo(
    () => keepingUp(issues, builds.map((b) => b.date ?? ''), now, WINDOW_DAYS),
    [issues, builds, now]
  );
  /* Blocked — the count, the longest-waiting one, and the single blocker that
     frees the most if cleared (the board's own "highest leverage" reading). */
  const blocked = useMemo(() => {
    // Count only ACTIVE cards something is REALLY still blocking — exclude
    // finished work (the tile is about live blocked work), and stillBlocked clears
    // a card whose named blocker is finished, so the tile never counts a cleared
    // dependency or names a finished card as the oldest block (L159). `frees`
    // already reads the leverage grouping, which filters by stillBlocked too.
    const list = issues.filter(
      (i) => i.status !== 'done' && i.status !== 'wontdo' && stillBlocked(i, issues),
    );
    const aged = list
      .filter((i) => typeof i.blockedAt === 'number')
      .map((i) => ({ ref: i.ref, days: Math.floor((now - (i.blockedAt as number)) / DAY) }))
      .sort((a, b) => b.days - a.days);
    const top = unblockActions(issues).find((a) => a.exact && a.blocker);
    return { count: list.length, oldest: aged[0], frees: top ? { ref: top.blocker!.ref, n: top.issues.length } : null };
  }, [issues, now]);

  /* RUN vs CHANGE — the board's work mix, by card count (#435). The one-word class
     (Change / Run / Mixed) reads in the header folded OR open; the split bar is a
     tile below. Derived live from every card's type + any per-card override, so
     there is nothing to keep up by hand; null label on an empty board hides both. */
  // The lifecycle default an untyped card's class falls back to — the SAME tier the Portfolio
  // bands by (project signals = tracked; continuous or handed-over = at service), so the
  // board's own mix and the Portfolio's can never disagree on an untyped board (Q3, F7).
  const invTier = boardLifecycleTier({ tracked: inferred === 'project', atService: inService || !isProject });
  const mix = useMemo(
    () => boardInvestmentMix(issues, issueTypes, investmentLifecycleDefault(invTier)),
    [issues, issueTypes, invTier],
  );

  const pillLabel = mode === 'board' ? 'Measure as a Board' : 'Measure as a Project';

  const header = (
    <div className="bm-head">
      <div className="bm-head-left">
        <span className="bm-title"><Icon name="chart" size={15} /> {inService ? 'Service Metrics' : 'Board Metrics'}</span>
        {mix.label && (
          <span
            data-reveal="work-mix"
            className={`bm-mix-chip ${mix.label === 'Change' ? 'is-change' : mix.label === 'Run' ? 'is-run' : 'is-mixed'}`}
            title={`Work mix, by card count: ${mix.changePct}% Change (building something new), ${mix.runPct}% Run (keeping the service running). Open the strip for the split and how to reclassify a card.`}
          >
            <span className="bm-mix-mark" aria-hidden="true">
              {(mix.label === 'Change' || mix.label === 'Mixed') && <i className="is-change" />}
              {(mix.label === 'Run' || mix.label === 'Mixed') && <i className="is-run" />}
            </span>
            {mix.label}
          </span>
        )}
        {isProject && (<>
        <div className="bm-mode" ref={pill}>
          <button
            type="button"
            className={`bm-pill${menuOpen ? ' open' : ''}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {pillLabel}
            <Icon name="chevron" size={18} />
          </button>
          {menuOpen && (
            <div className="bm-menu" role="menu">
              {(['board', 'project'] as Mode[]).map((m) => (
                <button
                  key={m}
                  role="menuitemradio"
                  aria-checked={mode === m}
                  className={`bm-menu-item${mode === m ? ' is-current' : ''}`}
                  onClick={() => { setMode(m); setMenuOpen(false); }}
                >
                  <span className="bm-menu-tick" aria-hidden="true">{mode === m ? '✓' : ''}</span>
                  {m === 'board' ? 'Measure as a Board' : 'Measure as a Project'}
                </button>
              ))}
            </div>
          )}
        </div>
        <InfoTip label="Measure as a Board or Project">
          Switch how this board is measured. Measure as a Board for ongoing flow — throughput and
          cycle time; Measure as a Project once the work has a plan, phases and a finish, to track
          it to delivery. Change it back any time. To change how this board actually runs — and show
          or hide the Plan and Finances — go to Settings → How this board is run.
        </InfoTip>
        </>)}
      </div>
      <div className="bm-head-right">
        {!folded && <span className="bm-window">Rolling {WINDOW_DAYS} days</span>}
        {!folded && (
          <button
            type="button"
            className="link bm-open"
            /* drillTo, not setView, so Insights arrives with a "← Back to the board"
               trail — setView clears cameFrom and stranded the round trip (workflow
               witness, 24 Sep). It also forces this board's scope. */
            onClick={() => { setInsightsScope('board'); drillTo('dashboard'); }}
          >
            Open Full Metrics <Icon name="chevron" size={14} />
          </button>
        )}
        {/* A project-run board folds this strip because that team works in the
            Plan — so the folded bar must SAY where that is, or selecting "Measure as a
            project" leaves a collapsed bar with no direction (workflow witness). */}
        {folded && mode === 'project' && (
          <button type="button" className="link bm-plan" onClick={() => setView('plan')}>
            See Plan <Icon name="chevron" size={14} />
          </button>
        )}
        <button type="button" className="link bm-fold" onClick={() => setFold(folded ? 'open' : 'folded')}>
          {folded ? 'Show Metrics' : 'Hide Metrics'}
        </button>
      </div>
    </div>
  );

  /* A rolling window can turn up nothing in flight (a fresh or quiet board). Say
     so plainly rather than drawing a hero of zeroes. */
  const hasFlight = flight.total > 0;

  /* Verbs match the bar labels (Finished / Raised) and stay parallel, so the
     note reads with the bars rather than against them (copy witness, 25 Sep). */
  const netNote =
    keep.growth < 0
      ? 'Finishing faster than raising — backlog shrinking.'
      : keep.growth > 0
        ? 'Raising faster than finishing — backlog growing.'
        : 'Finishing about as fast as raising.';

  return (
    <section className={`bm-strip${folded ? ' is-folded' : ''}`}>
      {header}

      {/* The body opens and closes on the same measured-height Expander every other
          panel uses (Insights reveals, the epic drawer), so the Board Metrics strip
          folds with the same smooth slide rather than snapping in and out (Vinnie,
          25 Oct). Kept mounted while folded — inert and height 0 — so it can animate
          both ways. */}
      <Expander open={!folded}>
      {/* `aging-hero` too, so the shared `.aging-hero .ah-body` flex layout (the
          summary | buckets | longest three-up) applies here exactly as on Insights. */}
      <div className="bm-hero aging-hero">
        <div className="bm-hero-lead">
          <p className="bm-hero-eyebrow"><Icon name="clock" size={13} /> Aging work in progress</p>
        </div>
        {hasFlight ? (
          <AgingHeroBody flight={flight} canDrill onOpen={openIssue} onShow={showInBacklog} />
        ) : (
          <p className="hint">Nothing is being worked on right now.</p>
        )}
      </div>

      <div className="bm-tiles">
        {/* Work mix — Change vs Run, by card count (#435). A two-tone split bar
            (petrol Change, steel Run) reads the mix at a glance; the legend names
            each with its % and count. Derived live; nothing to maintain by hand. */}
        <div className="bm-tile">
          <p className="bm-tile-label">
            Work Mix · by card count
            <InfoTip label="Work Mix">
              This board’s <b>Change-vs-Run</b> mix, by card count. A feature or improvement is{' '}
              <b>Change</b> (investment); a fix or upkeep is <b>Run</b> (business-as-usual). A card
              follows its type — to change one, open it → <b>Investment class</b>.
              <br />
              <br />
              Example: a “patch file servers” card is Run; a “new VPN gateway” is Change.
              <br />
              <br />
              Every board’s mix rolls into one split under Portfolio → Projects.
            </InfoTip>
          </p>
          {mix.total === 0 ? (
            <p className="bm-tile-note">Add cards to see the split between building (Change) and keeping the service running (Run).</p>
          ) : (
            <>
              <div
                className="bm-mix-bar"
                role="img"
                aria-label={`${mix.changePct}% Change (${mix.change} ${mix.change === 1 ? 'card' : 'cards'}), ${mix.runPct}% Run (${mix.run} ${mix.run === 1 ? 'card' : 'cards'}), by card count`}
              >
                {mix.change > 0 && <span className="bm-mix-seg is-change" style={{ width: `${mix.changePct}%` }} />}
                {mix.run > 0 && <span className="bm-mix-seg is-run" style={{ width: `${mix.runPct}%` }} />}
              </div>
              <div className="bm-mix-legend">
                <span className="bm-mix-key"><span className="bm-mix-swatch is-change" /> <strong>{mix.changePct}%</strong> Change · {mix.change}</span>
                <span className="bm-mix-key"><span className="bm-mix-swatch is-run" /> <strong>{mix.runPct}%</strong> Run · {mix.run}</span>
              </div>
            </>
          )}
        </div>

        <div className="bm-tile">
          <p className="bm-tile-label">Throughput</p>
          <p className="bm-tile-big"><span className="bm-tile-num">{Math.round(tp.perWeek)}</span> / week</p>
          <BmSpark series={tp.series} />
          <p className="bm-tile-note">
            {tp.trend === 'steady'
              ? `Steady over ${tp.weeks} weeks.`
              : tp.trend === 'speeding'
                ? `Speeding up over ${tp.weeks} weeks.`
                : `Slowing over ${tp.weeks} weeks.`}
          </p>
        </div>

        <div className="bm-tile">
          <p className="bm-tile-label">Cycle time</p>
          {cyc.sample === 0 ? (
            <p className="bm-tile-note">No finished cards to time in the last {cyc.days} days yet.</p>
          ) : (
            <>
              <p className="bm-tile-big">
                {cyc.median >= 1
                  ? <><span className="bm-tile-num">{cyc.median}</span> {cyc.median === 1 ? 'day' : 'days'}</>
                  : <><span className="bm-tile-num">&lt;1</span> day</>}
              </p>
              {cyc.delta !== null && cyc.delta !== 0 ? (
                <p className={`bm-tile-trend ${cyc.delta < 0 ? 'is-good' : 'is-warn'}`}>
                  {cyc.delta < 0
                    ? <>&#9660; {-cyc.delta} {(-cyc.delta) === 1 ? 'day' : 'days'} faster than the previous month.</>
                    : <>&#9650; {cyc.delta} {cyc.delta === 1 ? 'day' : 'days'} slower than the previous month.</>}
                </p>
              ) : (
                <p className="bm-tile-note">Median start &rarr; done.</p>
              )}
            </>
          )}
        </div>

        {/* Lead time — the split bar only (the board's compact read; the month-trend
            lives on the full Insights panel). Grey wait, then green work, so the
            backlog wait reads before any words. Same derived medians as Insights. */}
        <div className="bm-tile">
          <p className="bm-tile-label">Lead time</p>
          {lead.sample === 0 ? (
            <p className="bm-tile-note">No finished cards to time in the last {lead.days} days yet.</p>
          ) : (
            <>
              <p className="bm-tile-big">
                {lead.median >= 1
                  ? <><span className="bm-tile-num">{lead.median}</span> {lead.median === 1 ? 'day' : 'days'}</>
                  : <><span className="bm-tile-num">&lt;1</span> day</>}
              </p>
              <div
                className="bm-lead-bar"
                role="img"
                aria-label={`Of ${lead.median} days raised to done, about ${lead.waitMedian} waiting in the backlog then ${lead.workMedian} being worked`}
              >
                <span className="bm-lead-seg is-wait" style={{ width: `${(lead.waitMedian / Math.max(1, lead.waitMedian + lead.workMedian)) * 100}%` }} />
                <span className="bm-lead-seg is-work" style={{ width: `${(lead.workMedian / Math.max(1, lead.waitMedian + lead.workMedian)) * 100}%` }} />
              </div>
              <div className="bm-lead-legend">
                <span className="bm-lead-key"><span className="bm-lead-swatch is-wait" /> Waiting {lead.waitMedian}d</span>
                <span className="bm-lead-key"><span className="bm-lead-swatch is-work" /> Working {lead.workMedian}d</span>
              </div>
            </>
          )}
        </div>

        <div className="bm-tile">
          <p className="bm-tile-label">Net flow · {WINDOW_DAYS}d</p>
          <p className="bm-tile-big"><span className="bm-tile-num">{keep.growth > 0 ? '+' : keep.growth < 0 ? '−' : ''}{Math.abs(keep.growth)}</span> backlog</p>
          <div className="bm-flow">
            <div className="bm-flow-row">
              <span className="bm-flow-lbl">Finished</span>
              <span className="bm-flow-track"><span className="bm-flow-fill is-finished" style={{ width: `${(keep.finished / Math.max(1, keep.finished, keep.raised)) * 100}%` }} /></span>
              <span className="bm-flow-n">{keep.finished}</span>
            </div>
            <div className="bm-flow-row">
              <span className="bm-flow-lbl">Raised</span>
              <span className="bm-flow-track"><span className="bm-flow-fill is-raised" style={{ width: `${(keep.raised / Math.max(1, keep.finished, keep.raised)) * 100}%` }} /></span>
              <span className="bm-flow-n">{keep.raised}</span>
            </div>
          </div>
          <p className="bm-tile-note">{netNote}</p>
        </div>

        <div className="bm-tile">
          <p className="bm-tile-label">Likely finish</p>
          {keep.weeksToClear !== null ? (
            <>
              <p className="bm-tile-big"><span className="bm-tile-num">~{keep.weeksToClear}</span> {keep.weeksToClear === 1 ? 'week' : 'weeks'}</p>
              <p className="bm-tile-note">
                At this rate the {keep.open} open clear in about {keep.weeksToClear} {keep.weeksToClear === 1 ? 'week' : 'weeks'}. A flow forecast, not a deadline.
              </p>
            </>
          ) : (
            <>
              <p className="bm-tile-big"><span className="bm-tile-num">—</span></p>
              <p className="bm-tile-note">Nothing has finished recently, so there is no rate to forecast the {keep.open} open by.</p>
            </>
          )}
        </div>

        <div className="bm-tile">
          <p className="bm-tile-label">Blocked</p>
          <p className="bm-tile-big"><span className="bm-tile-num">{blocked.count}</span></p>
          <p className="bm-tile-note">
            {blocked.count === 0 ? 'Nothing is blocked.' : (
              <>
                {blocked.oldest && <>Oldest {blocked.oldest.days} {blocked.oldest.days === 1 ? 'day' : 'days'} ({blocked.oldest.ref}). </>}
                {blocked.frees && <>Clearing {blocked.frees.ref} frees {blocked.frees.n} {blocked.frees.n === 1 ? 'card' : 'cards'}.</>}
              </>
            )}
          </p>
        </div>
      </div>
      </Expander>
    </section>
  );
}

/** The throughput tile's mini sparkline — the same line as the Insights panel,
 *  sized for a tile. The full panel (with the per-week hovercards) is one click
 *  away behind "Open full metrics", so this stays a plain shape. */
function BmSpark({ series }: { series: { from: number; n: number }[] }) {
  const W = 100, H = 22, pad = 2;
  const max = Math.max(1, ...series.map((s) => s.n));
  const span = series.length > 1 ? (W - pad * 2) / (series.length - 1) : 0;
  const xy = (n: number, i: number) => ({ x: pad + i * span, y: pad + (1 - n / max) * (H - pad * 2) });
  const pts = series.map((s, i) => { const p = xy(s.n, i); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; });
  const end = series.length ? xy(series[series.length - 1].n, series.length - 1) : { x: 0, y: 0 };
  return (
    <svg className="spark bm-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={pts.join(' ')} fill="none" />
      <circle cx={end.x} cy={end.y} r="2" />
    </svg>
  );
}
